import type { CSSProperties } from 'react'
import { appConfig } from '@/config/app'
import type { TelegramSafeAreaInset, TelegramWebApp } from '@/types/telegram'
import { isTelegramMiniApp } from '@/utils/runtime'

type SafeAreaSide = 'top' | 'right' | 'bottom' | 'left'
type SafeAreaInsetKind = 'content' | 'device'

type SafeAreaProfileConfig = typeof appConfig.ui.safeArea.telegram

const resolveSafeAreaProfile = (): SafeAreaProfileConfig =>
  isTelegramMiniApp() ? appConfig.ui.safeArea.telegram : appConfig.ui.safeArea.browser

const safeAreaInsetVar = (side: SafeAreaSide, insetKind: SafeAreaInsetKind): string => {
  const prefix = insetKind === 'device' ? '--app-device-safe-area' : '--app-safe-area'
  return `var(${prefix}-${side}, 0px)`
}

const safeAreaCalc = (side: SafeAreaSide, basePx: number, insetKind: SafeAreaInsetKind): string => {
  return `calc(${safeAreaInsetVar(side, insetKind)} + ${basePx}px)`
}

export const buildSafeAreaPaddingStyle = (params: {
  topPx?: number
  rightPx?: number
  bottomPx?: number
  leftPx?: number
  insetKind?: SafeAreaInsetKind
}): CSSProperties => {
  const { topPx = 0, rightPx = 0, bottomPx = 0, leftPx = 0, insetKind = 'content' } = params

  return {
    paddingTop: safeAreaCalc('top', topPx, insetKind),
    paddingRight: safeAreaCalc('right', rightPx, insetKind),
    paddingBottom: safeAreaCalc('bottom', bottomPx, insetKind),
    paddingLeft: safeAreaCalc('left', leftPx, insetKind),
  }
}

export const buildSafeAreaBottomInsetStyle = (
  bottomPx: number,
  insetKind: SafeAreaInsetKind = 'content'
): CSSProperties => {
  return {
    paddingBottom: safeAreaCalc('bottom', bottomPx, insetKind),
  }
}

const buildSafeAreaStyles = () => {
  const safeAreaConfig = resolveSafeAreaProfile()

  return {
    mainScreen: buildSafeAreaPaddingStyle({
      topPx: safeAreaConfig.mainScreenTopPaddingPx,
      rightPx: safeAreaConfig.mainScreenHorizontalPaddingPx,
      bottomPx: safeAreaConfig.mainScreenBottomPaddingPx,
      leftPx: safeAreaConfig.mainScreenHorizontalPaddingPx,
    }),
    rootWithBottomNav: buildSafeAreaPaddingStyle({
      topPx: safeAreaConfig.rootWithBottomNavTopPaddingPx,
      bottomPx: safeAreaConfig.rootWithBottomNavBottomPaddingPx,
    }),
    appLayoutHeader: buildSafeAreaPaddingStyle({
      topPx: safeAreaConfig.appLayoutHeaderTopPaddingPx,
      rightPx: safeAreaConfig.appLayoutHorizontalPaddingPx,
      leftPx: safeAreaConfig.appLayoutHorizontalPaddingPx,
    }),
    appLayoutMain: buildSafeAreaPaddingStyle({
      topPx: safeAreaConfig.appLayoutMainTopPaddingPx,
      rightPx: safeAreaConfig.appLayoutHorizontalPaddingPx,
      bottomPx: safeAreaConfig.appLayoutMainBottomPaddingPx,
      leftPx: safeAreaConfig.appLayoutHorizontalPaddingPx,
    }),
    mobileMenu: buildSafeAreaBottomInsetStyle(safeAreaConfig.mobileMenuBottomSafeOffsetPx, 'device'),
    bottomNav: buildSafeAreaBottomInsetStyle(safeAreaConfig.bottomNavBottomSafeOffsetPx, 'device'),
  } as const
}

export const safeAreaStyles = buildSafeAreaStyles()

const toSafeInset = (value: unknown): number => {
  const numeric = Number(value)
  if (!Number.isFinite(numeric)) return 0
  return Math.max(0, numeric)
}

const pickInset = (
  side: SafeAreaSide,
  contentSafeAreaInset?: TelegramSafeAreaInset,
  safeAreaInset?: TelegramSafeAreaInset
): number => {
  const contentValue = toSafeInset(contentSafeAreaInset?.[side])
  const safeValue = toSafeInset(safeAreaInset?.[side])
  return Math.max(contentValue, safeValue)
}

export const resolveTelegramSafeAreaInsets = (webApp: TelegramWebApp): TelegramSafeAreaInset => {
  return {
    top: pickInset('top', webApp.contentSafeAreaInset, webApp.safeAreaInset),
    right: pickInset('right', webApp.contentSafeAreaInset, webApp.safeAreaInset),
    bottom: pickInset('bottom', webApp.contentSafeAreaInset, webApp.safeAreaInset),
    left: pickInset('left', webApp.contentSafeAreaInset, webApp.safeAreaInset),
  }
}

export const resolveTelegramDeviceSafeAreaInsets = (webApp: TelegramWebApp): TelegramSafeAreaInset => {
  return {
    top: toSafeInset(webApp.safeAreaInset?.top),
    right: toSafeInset(webApp.safeAreaInset?.right),
    bottom: toSafeInset(webApp.safeAreaInset?.bottom),
    left: toSafeInset(webApp.safeAreaInset?.left),
  }
}

export const applySafeAreaCssVariables = (params: {
  contentInsets: TelegramSafeAreaInset
  deviceInsets: TelegramSafeAreaInset
}): void => {
  if (typeof document === 'undefined') {
    return
  }

  const { contentInsets, deviceInsets } = params

  const style = document.documentElement.style
  style.setProperty('--app-safe-area-top', `${contentInsets.top ?? 0}px`)
  style.setProperty('--app-safe-area-right', `${contentInsets.right ?? 0}px`)
  style.setProperty('--app-safe-area-bottom', `${contentInsets.bottom ?? 0}px`)
  style.setProperty('--app-safe-area-left', `${contentInsets.left ?? 0}px`)

  style.setProperty('--app-device-safe-area-top', `${deviceInsets.top ?? 0}px`)
  style.setProperty('--app-device-safe-area-right', `${deviceInsets.right ?? 0}px`)
  style.setProperty('--app-device-safe-area-bottom', `${deviceInsets.bottom ?? 0}px`)
  style.setProperty('--app-device-safe-area-left', `${deviceInsets.left ?? 0}px`)
}
